import styled, { useTheme } from 'styled-components';
import Column from '../core/components/box/column';
import SizedBox from '../core/components/box/sizedBox';
import LXLowButton from '../core/components/buttons/lxLowButton';
import {
  Desktop,
  device,
  TabletAndBelow,
  useMedia,
} from '../core/designSystem/device';
import {
  LXbody,
  LXCaption,
  LXh1,
  LXh2,
  LXh3,
  LXh4,
} from '../core/texts/lxText';

import bg0 from './assets/serviceBg0.jpg';
import bg1 from './assets/serviceBg1.jpg';
import bg2 from './assets/serviceBg2.jpg';

const ContainerForPadding = styled.div`
  padding: 60px 25px 80px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${device.desktop} {
    padding: 80px 25px 100px;
  }
`;

interface BoardProps {
  maxWidth?: string;
  width?: string;
  height?: string;
  backgroundImage?: string;
  backgroundColor?: string;
}

const Board = styled.div<BoardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 40px 30px;
  width: ${(props) => props.width};
  max-width: 1040px;
  height: ${(props) => props.height ?? 'auto'};
  max-width: ${(props) => props.maxWidth ?? undefined};

  background-image: ${(props) => props.backgroundImage ?? undefined};
  background-color: ${(props) => props.backgroundColor ?? undefined};
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 12px;

  @media ${device.desktop} {
    padding: 50px 60px;
    height: 400px;
  }
`;

const ServicesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media ${device.desktop} {
    flex-direction: row;
  }
`;

interface ServicesProps {}
const Services = (props: ServicesProps) => {
  const { isTabletAndBelow } = useMedia();
  const theme = useTheme();

  const Title = ({ main, sub }: { main: string; sub: string }) => (
    <>
      <Desktop>
        <>
          <LXh1 children={main} />
          <SizedBox height={4} />
          <LXh4 fontWeight={400} children={sub} color={theme.colors.caption} />
        </>
      </Desktop>
      <TabletAndBelow>
        <>
          <LXh2 children={main} />
          <SizedBox height={4} />
          <LXbody children={sub} color={theme.colors.caption} />
        </>
      </TabletAndBelow>
    </>
  );

  const TextBox = ({
    title,
    text,
    color,
  }: {
    title: string;
    text: string;
    color: string;
  }) => {
    return (
      <>
        <LXCaption color={color} fontWeight={600} opacity={0.5}>
          {title}
        </LXCaption>
        <SizedBox height={15} />
        <LXh3
          fontWeight={400}
          color={color}
          width={'100%'}
          maxWidth={isTabletAndBelow ? '250px' : '392px'}
          textOverflow={'clip'}
          overflowWrap={'break-word'}
          overflow={'undefined'}
          whiteSpace={'pre-wrap'}
          height={'auto'}
        >
          {text}
        </LXh3>
      </>
    );
  };

  const Contents = () => {
    return (
      <>
        <Board width="100%" backgroundImage={`url(${bg0})`} height="480px">
          <TextBox
            color={theme.colors.white}
            title={theme.intro.aboutKLAB}
            text={theme.intro.describtionAboutKLAB}
          />
          <SizedBox height={30} />
          <LXLowButton
            disabled
            position="absolute"
            bottom={isTabletAndBelow ? 30 : 60}
            width={'133px'}
            fontSize={'13px'}
          >
            {theme.intro.learnMore}
          </LXLowButton>
        </Board>
        <SizedBox height={16} />
        <ServicesContainer>
          <Board backgroundImage={`url(${bg1})`} height={'310px'}>
            <TextBox
              color={theme.colors.title}
              title={theme.intro.oneStopSolution}
              text={theme.intro.describtionOneStopSolution}
            />
          </Board>
          {isTabletAndBelow ? (
            <SizedBox height={16} />
          ) : (
            <SizedBox width={16} />
          )}
          <Board backgroundImage={`url(${bg2})`} height={'310px'}>
            <TextBox
              color={theme.colors.title}
              title={theme.intro.worldClassHealthcarePartner}
              text={theme.intro.describtionWorldClassHealthcarePartner}
            />
          </Board>
        </ServicesContainer>
      </>
    );
  };

  return (
    <ContainerForPadding>
      <Column alignItems="center" width="100%">
        <Title
          main={theme.intro.clinicalLaboratoryService}
          sub={theme.intro.bySCLLXInternational}
        />
        <SizedBox height={isTabletAndBelow ? 30 : 50} />
        <Contents />
      </Column>
    </ContainerForPadding>
  );
};

export default Services;
